import { Autocomplete } from '@mui/material';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui/primitives';
import { Checkbox, TextField } from '@/ui/primitives';
import { filterInputSx } from './filterControlStyles';
import type { ReactElement } from 'react';

type TagOption = { value: string; label: string };

type TagsFilterFieldProps = {
  value: string[];
  options: TagOption[];
  label?: string;
  inputAriaLabel?: string;
  emptyLabel?: string;
  fullWidth?: boolean;
  onChange: (next: string[]) => void;
};

export const TagsFilterField = ({
  value,
  options,
  label = 'Tags',
  inputAriaLabel,
  fullWidth = false,
  onChange,
}: TagsFilterFieldProps): ReactElement => {
  const emptyOptionValue = '__flowqa_no_options__';
  const effectiveOptions =
    options.length > 0 ? options : [{ value: emptyOptionValue, label: 'No options' }];
  const selectedOptions = options.filter((option) => value.includes(option.value));
  const hasValue = selectedOptions.length > 0;
  const inputLeftInset = selectedOptions.length === 0 ? s('sm') : s('none');
  const fixedFilterWidth = `calc(${s('5xl')} + ${s('2xl')})`;
  const optionRowMinHeight = `calc(${s('2xl')} + ${s('xl')} + ${s('sm')})`;
  const optionHorizontalPadding = s('md');
  const optionCheckboxSlotWidth = `calc(${s('lg')} + ${s('sm')})`;
  const controlHeight = 32;
  const endAdornmentPadding =
    selectedOptions.length > 0 ? s('2xl') : `calc(${s('2xl')} + ${s('xxs')})`;
  const heightSx = {
    '& .MuiAutocomplete-inputRoot': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      maxHeight: `${controlHeight}px !important`,
      backgroundColor: 'background.paper',
      alignItems: 'center',
      flexWrap: 'nowrap !important',
      overflow: 'hidden',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingRight: `${endAdornmentPadding} !important`,
      minWidth: 0,
      '& .MuiAutocomplete-endAdornment': {
        right: s('none'),
        display: 'flex',
        alignItems: 'center',
        gap: s('none'),
        '& > button + button': {
          marginLeft: `calc(${s('xxs')} - ${s('md')}) !important`,
        },
      },
      '& .MuiAutocomplete-clearIndicator': {
        margin: `${s('none')} !important`,
        marginRight: `${s('none')} !important`,
        padding: `${s('xxs')} !important`,
        width: 'auto',
        height: 'auto',
        opacity: 1,
        visibility: 'visible',
        pointerEvents: 'auto',
      },
      '& .MuiAutocomplete-popupIndicator': {
        padding: `${s('xxs')} !important`,
        margin: `${s('none')} !important`,
        marginRight: `${s('none')} !important`,
        width: 'auto',
        height: 'auto',
      },
    },
    '& .MuiAutocomplete-input': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      minWidth: hasValue ? '2ch !important' : undefined,
      width: hasValue ? '2ch !important' : 'auto !important',
      flex: hasValue ? '0 0 2ch' : '1 1 auto',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingLeft: `${inputLeftInset} !important`,
      marginLeft: `${s('none')} !important`,
      lineHeight: '1 !important',
    },
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: s('md'),
        minWidth: 0,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      <Text role="label" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
        {label}
      </Text>
      <Box
        sx={{
          width: fullWidth ? '100%' : fixedFilterWidth,
          minWidth: fullWidth ? 0 : fixedFilterWidth,
          maxWidth: fullWidth ? 'none' : fixedFilterWidth,
          flex: fullWidth ? '1 1 0' : `0 0 ${fixedFilterWidth}`,
        }}
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          fullWidth={false}
          options={effectiveOptions}
          value={selectedOptions}
          isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) => option.value === emptyOptionValue}
          onChange={(_event, nextOptions) => {
            onChange(
              nextOptions
                .map((option) => option.value)
                .filter((optionValue) => optionValue !== emptyOptionValue),
            );
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const isEmptyOption = option.value === emptyOptionValue;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: s('sm'),
                  px: s('none'),
                  py: s('md'),
                  minHeight: optionRowMinHeight,
                }}
              >
                {isEmptyOption ? (
                  <Box
                    aria-hidden="true"
                    sx={{ width: optionCheckboxSlotWidth, minWidth: optionCheckboxSlotWidth }}
                  />
                ) : (
                  <Checkbox
                    size="small"
                    checked={selected}
                    sx={{
                      p: s('none'),
                      m: s('none'),
                      mr: s('xs'),
                      '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                    }}
                  />
                )}
                <Text role="body" component="span">
                  {option.label}
                </Text>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              inputHeight={controlHeight}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
              inputProps={{
                ...params.inputProps,
                'aria-label': inputAriaLabel ?? label,
              }}
            />
          )}
          sx={(theme) => ({
            ...(typeof filterInputSx === 'function' ? filterInputSx(theme) : {}),
            ...heightSx,
            width: '100%',
            minWidth: 0,
            ...(hasValue
              ? {
                  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }
              : {}),
            '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
              ...theme.typography.body1,
              fontSize: '14px',
            },
            '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
              ...theme.typography.body1,
              fontSize: '14px',
            },
            '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
              {
                ...theme.typography.body1,
                fontSize: '14px',
                color: theme.palette.text.primary,
                opacity: 1,
              },
          })}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
              },
            },
            listbox: {
              sx: {
                py: s('xxs'),
                '& .MuiAutocomplete-option': {
                  paddingLeft: `${optionHorizontalPadding} !important`,
                  paddingRight: `${optionHorizontalPadding} !important`,
                },
              },
            },
          }}
          renderValue={(selected) => {
            const selectedValues = Array.isArray(selected) ? selected : [];
            if (selectedValues.length === 0) return null;
            return (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  minWidth: 0,
                  maxWidth: '100%',
                  pl: s('xs'),
                  pr: s('none'),
                  color: 'text.primary',
                  font: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  lineHeight: `${controlHeight}px`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {selectedValues.map((option) => option.label).join(', ')}
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export type { TagOption, TagsFilterFieldProps };
